<template>
	<Col class="width-100 edit-workflows-container">
		<Row class="flex-align-center flex-justify-space-between mb-12">
			<Col>
				<h3 class="reg section-title">Add or remove add-ons from this dropdown</h3>
				<h3 class="title-description">Add, remove and reorder add-ons. Add-on can only belong to one dropdown.</h3>
			</Col>
			<AnemicButton :disabled="!hasEditPermissions" @click="openWorkflowsSelectionDialog" class="add-button px-4" tile>
				+ Add an add-on
			</AnemicButton>
		</Row>
		<Col :key="reRenderDraggableArea" class="width-100 workflows-container" v-if="dropdownWorkflowsIds.length > 0">
			<Container drag-handle-selector=".drag-handle" :class="{ disabled: isLoading }" class="width-100" @drop="onDrop">
				<template v-if="hasEditPermissions">
					<Draggable class="width-100" v-for="workflowId in dropdownWorkflowsIds" :key="workflowId">
						<ButtonDropdownWorkflowCard
							:workflowId="workflowId"
							@remove="() => removeOption(workflowId)"
							:hasEditPermissions="hasEditPermissions"
						/>
					</Draggable>
				</template>
				<template v-else>
					<ButtonDropdownWorkflowCard
						v-for="workflowId in dropdownWorkflowsIds"
						:key="workflowId"
						:workflowId="workflowId"
						@remove="() => removeOption(workflowId)"
						:hasEditPermissions="hasEditPermissions"
					/>
				</template>
			</Container>
			<Col v-if="isLoading" class="loader-container width-100 height-100 flex-centerize">
				<AppLoader />
			</Col>
		</Col>
		<h3 v-else class="reg empty-container flex-centerize">No add-ons added yet</h3>
	</Col>
</template>

<script>
import { applyDrag } from '@/utils/helpers';
import { Container, Draggable } from 'vue-smooth-dnd';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import { updateButtonDropdown } from '@/store/modules/buttonDropdowns/actions';
import ButtonDropdownWorkflowCard from '@/components/ButtonsDropdown/ButtonDropdownWorkflowCard.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { FeatureType } from '@/components/CreationWizard/Steps/ChooseProjectAndFeatureType/FeatureTypeSelector/feature-type-selector.const';
import { getWorkflows } from '@/store/modules/workflows/actions';
import { getWorkflow } from '@/store/modules/workflows/getters';
import { UIElementType } from '@ply-io/ply-front-shared-utilities';

export default {
	name: 'EditButtonsDropdownWorkflows',
	components: {
		Container,
		Draggable,
		ButtonDropdownWorkflowCard,
	},
	props: {
		buttonDropdown: { type: Object },
		hasEditPermissions: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ getWorkflow }),
		dropdownWorkflowsIds() {
			return this.buttonDropdown.workflows || [];
		},
		filterBy() {
			return {
				project: this.buttonDropdown.project,
				feature_type: FeatureType.AddOn,
				add_on_type: UIElementType.Button,
				placement: this.buttonDropdown.placement,
			};
		},
	},
	methods: {
		...mapActionsShorthand({ updateButtonDropdown, getWorkflows }),
		removeOption(workflowId) {
			this.updateButtonDropdownWorkflows(this.dropdownWorkflowsIds.filter((id) => id !== workflowId));
		},
		onDrop(dropResult) {
			this.updateButtonDropdownWorkflows(applyDrag(this.dropdownWorkflowsIds, dropResult));
		},
		async updateButtonDropdownWorkflows(newWorkflowsIds) {
			this.isLoading = true;
			try {
				await this.updateButtonDropdown({ id: this.buttonDropdown.id, data: { workflows: newWorkflowsIds } });
			} catch {
				this.$toast.error('Something went wrong, please try again later.');
			} finally {
				this.isLoading = false;
				this.reRenderDraggableArea++;
			}
		},
		async openWorkflowsSelectionDialog() {
			const res = await AppDialogManagerService.openWorkflowsSelectionDialog({
				filterBy: this.filterBy,
				currentButtonDropdownId: this.buttonDropdown.id,
				selectedWorkflowsIds: this.dropdownWorkflowsIds,
			});
			if (res?.workflows) {
				this.updateButtonDropdownWorkflows(res.workflows);
			}
		},
	},
	data() {
		return {
			isLoading: false,
			reRenderDraggableArea: 0,
		};
	},
	watch: {
		buttonDropdown: {
			async handler(v) {
				if (v) {
					const { workflows } = v;
					const hasMissingWorkflows = workflows.some((workflowId) => !this.getWorkflow(workflowId));
					if (hasMissingWorkflows) {
						this.isLoading = true;
						try {
							await this.getWorkflows({ autoPaginate: true, filterBy: { id: workflows } });
						} finally {
							this.isLoading = false;
						}
					}
				}
			},
			immediate: true,
		},
	},
};
</script>

<style scoped lang="scss">
.edit-workflows-container {
	overflow-y: auto;
	border-radius: 8px;
	background: var(--white);
	border: 1px solid #dfe4f0;
	padding: 12px 20px 38px 12px;
	max-height: calc(100vh - 423px);
}
.section-title {
	color: #4d4c51;
	font-weight: bold;
}
.title-description {
	color: #929292;
}
.add-button {
	height: 38px !important;
	font-size: 13px !important;
	border-radius: 6px !important;
	font-weight: normal !important;
	color: var(--white) !important;
	letter-spacing: 0px !important;
	text-transform: none !important;
	background: #6761ef !important;
}
::v-deep {
	.smooth-dnd-ghost {
		transform: translate(-50px, -50px) !important;
		margin-left: 0;
	}
}
.disabled {
	opacity: 0.2;
	pointer-events: none;
}
.loader-container {
	position: absolute;
}
.workflows-container {
	position: relative;
}
</style>
